import { Reveal } from "@components/ui";
import alertIcon from "@assets/images/noun-alert-8019270-1.svg";
import callIcon from "@assets/images/noun-call-2443021-1.svg";
import mechanicIcon from "@assets/images/noun-mechanic-4052754-1.svg";

const doItems = [
  "Turn off all gas appliances and main valve if accessible.",
  "Open windows and doors to ventilate the area.",
  "Evacuate everyone from the premises.",
  "Call our 24/7 Emergency Hotline 800-GAZ (42747) from a safe distance.",
  "If needed, contact Civil Defense (997).",
];

const dontItems = [
  "Don't ignite matches or lighters.",
  "Switch on/off any electrical devices.",
  "Don't use mobile phones near the leak site.",
  "Don't attempt to repair the leak yourself.",
];

const steps = [
  {
    icon: alertIcon,
    title: "Detect Gas Smell or Alarm",
    description: "Evacuate Immediately",
    color: "#ff2600",
  },
  {
    icon: callIcon,
    title: "Call 800-GAZ",
    description:
      "Our response team arrives within minutes in Dubai / Sharjah",
    color: "#122d63",
  },
  {
    icon: mechanicIcon,
    title: "Technician Inspection & Repair",
    description:
      "Leak isolated, tested, and service restored after approval",
    color: "#269d0e",
  },
];

function CheckCircle() {
  return (
    <div className="w-7 h-7 md:w-8 md:h-8 rounded-full bg-[#269d0e]/10 flex items-center justify-center shrink-0 mt-0.5">
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M3.5 8.5L6.5 11.5L12.5 4.5" stroke="#269d0e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </div>
  );
}

function XCircle() {
  return (
    <div className="w-7 h-7 md:w-8 md:h-8 rounded-full bg-[#ff2600]/10 flex items-center justify-center shrink-0 mt-0.5">
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M4.5 4.5L11.5 11.5M11.5 4.5L4.5 11.5" stroke="#ff2600" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </div>
  );
}

export function EmergencyActionsSection() {
  return (
    <section className="w-full bg-[#f1f1f1] overflow-hidden px-5 md:px-10 lg:px-[180px] py-12 md:py-16 lg:py-[120px]">
      <div className="w-full max-w-[1368px] mx-auto flex flex-col items-center gap-12 md:gap-16 lg:gap-20 font-albert-sans">
        {/* Title */}
        <Reveal direction="up">
          <div className="flex flex-col items-center gap-4 max-w-[1112px] mx-auto text-center">
            {/* Pill Badge */}
            <span className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-[#ff2600]/8 text-[#ff2600] text-[13px] md:text-[15px] font-semibold tracking-[-0.04em] uppercase">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 9V14M12 21.41H5.94C2.47 21.41 1.02 18.93 2.7 15.9L5.82 10.28L8.76 5.00003C10.54 1.79003 13.46 1.79003 15.24 5.00003L18.18 10.29L21.3 15.91C22.98 18.94 21.52 21.42 18.06 21.42H12V21.41Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M11.995 17H12.004" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Emergency
            </span>
            <h2 className="text-[28px] md:text-[44px] lg:text-[60px] font-semibold tracking-[-0.04em] text-[#122d63] leading-[1.1]">
              If You Smell Gas or Suspect a Leak,{" "}
              <br className="hidden md:block" />
              <span className="text-[#ff2600]">Act Immediately</span>
            </h2>
            <p className="text-[14px] md:text-base lg:text-[24px] tracking-[-0.04em] text-[#62636c] max-w-[974px]">
              Gas leaks can be dangerous. Follow these critical steps to ensure
              your safety and the safety of those around you
            </p>
          </div>
        </Reveal>

        {/* Do / Don't Cards */}
        <Reveal direction="up">
          <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-6 rounded-[16px] overflow-hidden">
            {/* Do Card */}
            <div className="group shadow-[0px_4px_24px_rgba(0,0,0,0.1)] rounded-[16px] bg-white flex flex-col items-start p-6 md:p-8 lg:p-12 gap-5 border border-transparent hover:border-[#e8e9ed] hover:shadow-[0_4px_20px_rgba(0,0,0,0.08)] transition-all duration-300">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 md:w-12 md:h-12 rounded-[12px] bg-[#269d0e]/8 flex items-center justify-center">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="#269d0e" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M7.75 12L10.58 14.83L16.25 9.17" stroke="#269d0e" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <h3 className="text-[24px] md:text-[32px] lg:text-[40px] font-semibold tracking-[-0.04em] text-[#269d0e] leading-[1.2]">
                  Do This Right Away
                </h3>
              </div>
              <div className="flex flex-col gap-4 md:gap-6">
                {doItems.map((item, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <CheckCircle />
                    <p className="text-[16px] md:text-[20px] lg:text-[24px] tracking-[-0.04em] text-[#1e1f24]">
                      {item}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Don't Card */}
            <div className="group shadow-[0px_4px_24px_rgba(0,0,0,0.1)] rounded-[16px] bg-white flex flex-col items-start p-6 md:p-8 lg:p-12 gap-5 h-full border border-transparent hover:border-[#e8e9ed] hover:shadow-[0_4px_20px_rgba(0,0,0,0.08)] transition-all duration-300">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 md:w-12 md:h-12 rounded-[12px] bg-[#ff2600]/8 flex items-center justify-center">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="#ff2600" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M9.17 14.83L14.83 9.17M14.83 14.83L9.17 9.17" stroke="#ff2600" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <h3 className="text-[24px] md:text-[32px] lg:text-[40px] font-semibold tracking-[-0.04em] text-[#ff2600] leading-[1.2]">
                  Do NOT
                </h3>
              </div>
              <div className="flex flex-col gap-4 md:gap-6">
                {dontItems.map((item, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <XCircle />
                    <p className="text-[16px] md:text-[20px] lg:text-[24px] tracking-[-0.04em] text-[#1e1f24]">
                      {item}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Reveal>

        {/* Divider */}
        <div className="w-full h-px bg-[#b9bbc6]" />

        {/* Response Steps */}
        <Reveal direction="up">
          <div className="w-full grid grid-cols-1 sm:grid-cols-3 gap-6">
            {steps.map((step, index) => (
              <div
                key={index}
                className="group rounded-[16px] bg-white flex flex-col items-center py-6 px-4 gap-6 md:gap-8 text-center border border-transparent hover:border-[#e8e9ed] hover:shadow-[0_4px_20px_rgba(0,0,0,0.08)] transition-all duration-300"
              >
                {/* Step Number */}
                <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-[#122d63] text-white text-[14px] font-bold">
                  {index + 1}
                </span>
                {/* Tinted Icon Container */}
                <div
                  className="w-16 h-16 md:w-20 md:h-20 rounded-[16px] flex items-center justify-center transition-transform duration-300 group-hover:scale-105"
                  style={{ backgroundColor: `${step.color}10` }}
                >
                  <img
                    src={step.icon}
                    alt=""
                    className="w-[40px] md:w-[50px] h-auto"
                  />
                </div>
                <div className="flex flex-col items-center gap-0.5">
                  <h4 className="text-[20px] md:text-[24px] lg:text-[28px] font-semibold tracking-[-0.04em] text-[#1e1f24]">
                    {step.title}
                  </h4>
                  <p className="text-[14px] md:text-[18px] lg:text-[20px] tracking-[-0.04em] text-[#62636c]">
                    {step.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
